/**
 * The index's `⋯` — exactly two acts, and a sentence saying why there are not three.
 *
 * **Bulk "gotowe" covers TRIAGE only.** A feedback item is a client waiting for the trainer's word;
 * clearing a column of them in one press says "handled" about work nobody did. Deferring is honest,
 * so "odłóż wszystko" takes everything. `model/taskDeck`'s `bulkTargets` is the rule; the sentence
 * under the two items is how the trainer learns it without having to discover the asymmetry.
 *
 * Each act is N idempotent single writes with ONE queue read after them — see `lib/useHeldResponses`.
 */

import { Menu } from '@base-ui/react/menu';
import { useTranslation } from 'react-i18next';

import menu from '~/shared/ui/MenuSurface.module.css';
import { MoreGlyph } from '~/shared/ui/MoreGlyph';

import type { BulkAct } from '../model/taskDeck';
import styles from './decisionBar.module.css';
import queue from './taskQueue.module.css';

interface BulkMenuProps {
  /** How many cards each act would cover. A zero disables that act rather than hiding it. */
  readonly doneCount: number;
  readonly snoozeCount: number;
  readonly onAct: (act: BulkAct) => void;
}

export function BulkMenu({ doneCount, snoozeCount, onAct }: BulkMenuProps) {
  const { t } = useTranslation();

  return (
    <Menu.Root>
      <Menu.Trigger className={queue.more} aria-label={t('web:tasks.bulk.trigger')}>
        <MoreGlyph className={queue.moreGlyph} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner className={menu.positioner} sideOffset={6} align="end">
          <Menu.Popup className={`${String(menu.popup)} ${String(styles.menu)}`}>
            <Menu.Group>
              {/* Inside the group: Base UI reads the group off context and THROWS when a
                * `Menu.GroupLabel` stands beside one. See `DecisionBar`. */}
              <Menu.GroupLabel className={`${String(menu.groupLabel)} ${String(styles.menuLabel)}`}>
                {t('web:tasks.bulk.header')}
              </Menu.GroupLabel>
              <Menu.Item
                className={menu.item}
                disabled={doneCount === 0}
                onClick={() => {
                  onAct('done');
                }}
              >
                <span className={menu.itemLabel}>{t('web:tasks.bulk.done')}</span>
                <span className={menu.itemHint}>{doneCount}</span>
              </Menu.Item>
              <Menu.Item
                className={menu.item}
                disabled={snoozeCount === 0}
                onClick={() => {
                  onAct('snoozed');
                }}
              >
                <span className={menu.itemLabel}>{t('web:tasks.bulk.snooze')}</span>
                <span className={menu.itemHint}>{snoozeCount}</span>
              </Menu.Item>
            </Menu.Group>
            {/* Not an item: it is the reason the first one says "zadania" rather than "wszystko". */}
            <p className={styles.bulkNote}>{t('web:tasks.bulk.note')}</p>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
